
import DateUtils from "@/utils/DateUtils.js";
import ZonedClock from "./ZonedClock.js";

/**
 * The base of every time unit, mirroring com.readychek.time.TimeUnit.
 *
 * Every field is read through the unit's own zone rather than the browser's, so a unit
 * carrying "America/Halifax" reports the Halifax wall clock wherever it runs. Arithmetic
 * is immutable throughout: plus and minus hand back a new unit and leave the receiver
 * alone, matching the java units.
 */
export default class TimeUnit {

  constructor(date, timeZone) {
    this._date = date instanceof Date ? date : new Date(date);
    this._timeZone = ZonedClock.safeZone(timeZone);
  }

  // ------------------------------------------------------------------ instant

  date() {
    return this._date;
  }

  /** The underlying instant, named as the java unit names it. */
  internalDate() {
    return this._date;
  }

  timeZone() {
    return this._timeZone;
  }

  /** The zone's id, matching TimeUnit.zone() in java. */
  zone() {
    return this._timeZone;
  }

  time() {
    return this._date.getTime();
  }

  isUnset() {
    return this.time() === 0;
  }

  isEmpty() {
    if (this.isUnset()) {
      return true;
    }
    return isNaN(this._date.getTime());
  }

  isLt(timeUnit) {
    return this.time() < timeUnit.time();
  }

  isGt(timeUnit) {
    return this.time() > timeUnit.time();
  }

  isLtEq(timeUnit) {
    return this.time() <= timeUnit.time();
  }

  isGtEq(timeUnit) {
    return this.time() >= timeUnit.time();
  }

  diff(timeUnit) {
    return this.time() - timeUnit.time();
  }

  // ------------------------------------------------------------ zoned fields

  /** The wall-clock fields of this instant, as read in this unit's zone. */
  parts() {
    return ZonedClock.partsIn(this._date, this._timeZone);
  }

  yearValue() {
    return this.parts().year;
  }

  monthValue() {
    return this.parts().month;
  }

  dayOfMonth() {
    return this.parts().day;
  }

  dayOfWeek() {
    return this.parts().weekday;
  }

  hourValue() {
    return this.parts().hour;
  }

  minute() {
    return this.parts().minute;
  }

  second() {
    return this.parts().second;
  }

  milli() {
    return this.parts().milli;
  }

  /** Whether this instant falls in the morning half of its own day. */
  am() {
    return this.hourValue() < 12;
  }

  static newTimeId(hour, quarter) {
    return hour * 4 + quarter;
  }

  // ---------------------------------------------------------------- display

  /** Zero pads a number to two digits. */
  static pad(value) {
    return value < 10 ? "0" + value : "" + value;
  }

  displayAs_YYYY_MM_DD() {
    if (this.isEmpty()) {
      return "";
    }
    const parts = this.parts();
    return parts.year + "-" + TimeUnit.pad(parts.month + 1) + "-" + TimeUnit.pad(parts.day);
  }

  displayAs_MM_DD(monthName) {
    if (this.isEmpty()) {
      return "";
    }
    const parts = this.parts();
    const month = monthName ? DateUtils.monthName(parts.month, true) : TimeUnit.pad(parts.month + 1);
    return month + "-" + TimeUnit.pad(parts.day);
  }

  displayAs_DD() {
    if (this.isEmpty()) {
      return "";
    }
    return TimeUnit.pad(this.parts().day);
  }

  displayAs_MM_YYYY() {
    if (this.isEmpty()) {
      return "";
    }
    const parts = this.parts();
    return TimeUnit.pad(parts.month + 1) + " " + parts.year;
  }

  displayAs_YYYY_MM_DD_HH_MM_SS() {
    if (this.isEmpty()) {
      return "";
    }
    return this.displayAs_YYYY_MM_DD() + " " + this.clock24();
  }

  displayAs_YYYY_MM_DD_HH_MM_SS_SSS() {
    if (this.isEmpty()) {
      return "";
    }
    const parts = this.parts();
    const millis = parts.milli < 100
      ? (parts.milli < 10 ? "00" + parts.milli : "0" + parts.milli)
      : "" + parts.milli;
    return this.displayAs_YYYY_MM_DD() + " " + this.clock24() + "." + millis;
  }

  displayAs_YYYY_MM_DD_HH_MM_SS_SSS_A() {
    if (this.isEmpty()) {
      return "";
    }
    return (this.displayAs_YYYY_MM_DD_HH_MM_SS_SSS() + " " + this.zoneShortName()).trim();
  }

  displayAs_YYYY_MM_DD_HH_MM_SS_A(notSetValue) {
    if (this.isEmpty()) {
      return notSetValue ? notSetValue : "";
    }
    return this.displayAs_YYYY_MM_DD() + " " + this.displayAs_HH_MM_SS_A();
  }

  displayAs_YYYY_MM_DD_H_MM_A() {
    if (this.isEmpty()) {
      return "";
    }
    return (this.displayAs_YYYY_MM_DD() + " " + this.displayAs_HH_MM_A()).trim();
  }

  displayAs_HH_MM_SS_A() {
    if (this.isEmpty()) {
      return "";
    }
    return (this.clock12(true) + " " + this.zoneShortName()).trim();
  }

  displayAs_HH_MM_SS() {
    if (this.isEmpty()) {
      return "";
    }
    return this.clock12(true);
  }

  displayAs_HH_MM_A() {
    if (this.isEmpty()) {
      return "";
    }
    return this.clock12(false);
  }

  /** The 24 hour clock reading, hh:mm:ss. */
  clock24() {
    const parts = this.parts();
    return TimeUnit.pad(parts.hour) + ":" + TimeUnit.pad(parts.minute) + ":" + TimeUnit.pad(parts.second);
  }

  /** The 12 hour clock reading, with or without seconds, e.g. "1:30:05 p.m.". */
  clock12(withSeconds) {
    const parts = this.parts();
    const meridiem = parts.hour >= 12 ? "p.m." : "a.m.";
    let hour = parts.hour % 12;
    if (hour === 0) {
      hour = 12;
    }
    const seconds = withSeconds ? ":" + TimeUnit.pad(parts.second) : "";
    return hour + ":" + TimeUnit.pad(parts.minute) + seconds + " " + meridiem;
  }

  zoneShortName() {
    return ZonedClock.zoneShortName(this._date, this._timeZone);
  }

  toString() {
    return this.displayAs_YYYY_MM_DD_HH_MM_SS_SSS_A();
  }

  static displayAs_HH_MM(decimalHours) {
    if (decimalHours == null || decimalHours == undefined) {
      return '';
    }
    if (decimalHours < 0) {
      return '';
    }
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return decimalHours < 1 ? `${minutes}min` : minutes === 0 ? `${hours}h` : `${hours}h ${minutes}min`;
  }
}
